import { toast } from "@gigpro/ui/Toast";
import {
  getLocationsPaymentMethodRetrieveQueryKey,
  getLocationsRetrieveQueryKey,
  getStafferOrganizationsPaymentMethodRetrieveQueryKey,
  getStafferOrganizationsRetrieveQueryKey,
  type PatchedOrganizationPartialUpdateRequest,
  type PatchedPlatformAdminLocationRequest,
  useLocationsPartialUpdate,
  useStafferOrganizationsPartialUpdate,
} from "@sidegigllc/api/client";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import type { SetupIntent, SetupIntentResult } from "@stripe/stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { captureException } from "utils/errorReporting";
import { toastApiError } from "utils/toastApiError";

/** Whose Stripe customer the payment method is saved to. */
export type PaymentSourceId = { locationId: number } | { organizationId: string };

type PaymentSourcePatch = PatchedPlatformAdminLocationRequest & PatchedOrganizationPartialUpdateRequest;

const ABSOLUTE_URL = /^(?:[a-z+]+:)?\/\//i;

export const isAbsoluteUrl = (url: string) => ABSOLUTE_URL.test(url);
const toAbsoluteUrl = (url: string) => (isAbsoluteUrl(url) ? url : location.origin + url);

/** PATCHes the source's payment settings, toasting the outcome and refreshing its billing queries. */
export function usePaymentSourceUpdate(sourceId: PaymentSourceId) {
  const queryClient = useQueryClient();
  const onError = (error: unknown) => toastApiError(error);

  const updateOrganization = useStafferOrganizationsPartialUpdate({
    mutation: {
      onSuccess: (_data, { id }) => {
        toast.success("Organization info updated successfully");
        queryClient.invalidateQueries({ queryKey: getStafferOrganizationsRetrieveQueryKey(id) });
        queryClient.invalidateQueries({ queryKey: getStafferOrganizationsPaymentMethodRetrieveQueryKey(id) });
      },
      onError,
    },
  });
  const updateLocation = useLocationsPartialUpdate({
    mutation: {
      onSuccess: (_data, { id }) => {
        toast.success("Location info updated successfully");
        queryClient.invalidateQueries({ queryKey: getLocationsRetrieveQueryKey(id) });
        queryClient.invalidateQueries({ queryKey: getLocationsPaymentMethodRetrieveQueryKey(id) });
      },
      onError,
    },
  });

  return async (data: PaymentSourcePatch) => {
    if ("organizationId" in sourceId) await updateOrganization.mutateAsync({ id: sourceId.organizationId, data });
    else await updateLocation.mutateAsync({ id: sourceId.locationId, data });
  };
}

const CONFIRM_FAILED_MESSAGE = "We couldn't save your payment method. Try again, or use a different one.";

/**
 * Confirms the PaymentElement's SetupIntent, then switches the source to instant charges.
 * Must render under `StripeElementsProvider`. Once confirmed, a retry only re-sends the PATCH (Stripe refuses a second
 * confirm), so consumers should make the PaymentElement `readOnly` while `isConfirmed`.
 */
export function useSaveStripePaymentMethod(sourceId: PaymentSourceId) {
  const stripe = useStripe();
  const elements = useElements();
  const updatePaymentSource = usePaymentSourceUpdate(sourceId);
  const [confirmed, setConfirmed] = React.useState<SetupIntent>();

  const confirm = async (email: string | undefined, returnUrl: string): Promise<SetupIntentResult> => {
    if (!stripe || !elements) throw new Error("Stripe is not ready");
    try {
      return await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: toAbsoluteUrl(returnUrl), payment_method_data: { billing_details: { email } } },
        redirect: "if_required",
      });
    } catch (error) {
      captureException(error);
      return { error: { type: "api_error", message: CONFIRM_FAILED_MESSAGE } };
    }
  };

  const save = async ({ email, returnUrl }: { email?: string; returnUrl: string }): Promise<SetupIntentResult> => {
    let setupIntent = confirmed;
    if (!setupIntent) {
      const result = await confirm(email, returnUrl);
      if (result.error) return result;
      setupIntent = result.setupIntent;
      setConfirmed(setupIntent);
    }
    await updatePaymentSource({ payment_source: "INSTANT_CHARGES" });
    return { setupIntent };
  };

  return { isReady: !!stripe && !!elements, isConfirmed: !!confirmed, save };
}
